import { memo, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { NewRedirectIcon } from 'components/customicons/holdings';
import Render from 'components/render';
import StripPortal, { STRIPS_ORDER } from 'components/StripPortal';
import {
  TWENTY_FOUR_HOURS,
  FIFTEEN_MINUTES,
  FIVE_MINUTES,
  SECONDS_IN_A_DAY,
  ONE_HOUR,
  UNDER_MAINTENANCE_URL,
  IS_RN_ANALYTICS_MARKETS,
  FOUR_HOURS,
  MAINTENANCE_BANNER_CLOSE_RULES,
  MAINTENANCE_BANNER_RULES,
} from 'constants/constants';
import { TIME_FORMATS } from 'constants/timeformats';
import {
  dateFormat,
  getDateTime,
  getDiff,
  getSeconds,
  startOf,
  timeNow,
  toLocal,
  toUTC,
} from 'helpers/day';
import {
  APP_DRAWER_TAB_ID_MAPPING,
  AppDrawerPossibleTabs,
} from 'reducers/initialstate/appDrawer';
import { activeTabSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import { SmallRectangle, CloseIcon } from 'styles/assets/icons';

import { MaintenanceStripProps } from '../DesktopView';
import styles from '../maintenanceBanner.module.scss';
import useMaintenance from '../useMaintenance';

const MobileView = ({ isTopBanner, className }: MaintenanceStripProps) => {
  const { t } = useTranslation(['common']);

  const drawerTab = useAppSelector(activeTabSelector) as AppDrawerPossibleTabs;

  const shouldDisplayInMobileTab = [
    APP_DRAWER_TAB_ID_MAPPING.HOME,
    APP_DRAWER_TAB_ID_MAPPING.POSITIONS,
    APP_DRAWER_TAB_ID_MAPPING.TRADE,
    APP_DRAWER_TAB_ID_MAPPING.MARKETS,
  ].includes(drawerTab as Exclude<AppDrawerPossibleTabs, 'tabChartOrderbook'>);

  const isStickyTab = [
    APP_DRAWER_TAB_ID_MAPPING.TRADE,
    APP_DRAWER_TAB_ID_MAPPING.POSITIONS,
  ].includes(
    drawerTab as Exclude<AppDrawerPossibleTabs, 'tabChartOrderbook' | 'markets' | 'home'>
  );

  const {
    isLoggedIn,
    showBanner,
    setShowBanner,
    maintenanceStartTime,
    maintenanceFinishTime,
    isDatesEqual,
    maintenanceDuration,
    timeLeftToStartMaintenance,
    isVisibleToUsers,
    countdownRef,
    maintenance_start_time: startTime,
    maintenanceFinishTimeInMilliseconds,
    maintenance_announcement_time: announcementTime,
    countdownTimer,
  } = useMaintenance();

  const { isTabInactive } = useAppSelector(state => state.other);
  const [isClosable, setClosable] = useState(true);

  const [bannerClosed, setBannerClosed] = useState(
    // eslint-disable-next-line no-unneeded-ternary
    localStorage.getItem('maintenanceStripClosingTime') ? true : false
  );

  useEffect(() => {
    if (localStorage.getItem('maintenanceStripClosingTime') && announcementTime) {
      const closingTime =
        localStorage.getItem('maintenanceStripClosingTime') &&
        JSON.parse(localStorage.getItem('maintenanceStripClosingTime')!);

      if (Number(closingTime) * 1000 < Number(announcementTime)) {
        localStorage.removeItem('maintenanceStripClosingTime');
        setBannerClosed(false);
      }
    }
  }, [announcementTime]);

  useEffect(() => {
    const maintenanceStripClosingTime =
      localStorage.getItem('maintenanceStripClosingTime') &&
      JSON.parse(localStorage.getItem('maintenanceStripClosingTime')!);

    const currentTime = timeNow();
    const timeDiff = getDiff(
      currentTime,
      getDateTime(maintenanceStripClosingTime),
      'seconds'
    );

    if (timeDiff >= 2 * SECONDS_IN_A_DAY) {
      setBannerClosed(false);
      localStorage.removeItem('maintenanceStripClosingTime');
    }
  }, [isTabInactive, drawerTab, startTime]);

  const setBannerVisibility = () => {
    if (
      !maintenanceDuration ||
      !timeLeftToStartMaintenance ||
      timeLeftToStartMaintenance <= 0
    ) {
      setShowBanner(false);
      return;
    }

    const tabRule = MAINTENANCE_BANNER_RULES[drawerTab];

    if (!tabRule) {
      setShowBanner(false);
      return;
    }

    let bannerThreshold;
    if (!isLoggedIn) {
      bannerThreshold = TWENTY_FOUR_HOURS;
    } else if (maintenanceDuration < ONE_HOUR) {
      bannerThreshold = tabRule.lessThan1h;
    } else if (maintenanceDuration <= FOUR_HOURS) {
      bannerThreshold = tabRule.between1And4h;
    } else {
      bannerThreshold = tabRule.moreThan4h;
    }

    setShowBanner(timeLeftToStartMaintenance <= bannerThreshold);
  };

  const storeMaintenanceStripCloseTime = () => {
    const currentTime = new Date().getTime();
    localStorage.setItem('maintenanceStripClosingTime', currentTime.toString());
  };
  const onCloseIconClick = () => {
    if (!maintenanceDuration) return;

    if (drawerTab !== 'tabtrade') {
      setBannerClosed(true);
      storeMaintenanceStripCloseTime();
      return;
    }

    const rule = MAINTENANCE_BANNER_CLOSE_RULES.home;
    const countdown = countdownRef?.current ?? 0;

    let closableThreshold;
    if (maintenanceDuration < ONE_HOUR) {
      closableThreshold = rule.lessThan1h;
    } else if (maintenanceDuration <= FOUR_HOURS) {
      closableThreshold = rule.between1And4h;
    } else {
      closableThreshold = rule.moreThan4h;
    }
    const insideBufferTime = countdown <= closableThreshold + FIVE_MINUTES;
    if (insideBufferTime) {
      setBannerClosed(true);
    } else {
      setBannerClosed(true);
      storeMaintenanceStripCloseTime();
    }
  };

  const showCrossButton = () => {
    if (!maintenanceDuration) return;

    const countdown = countdownRef?.current ?? 0;
    const rule = MAINTENANCE_BANNER_CLOSE_RULES[drawerTab];

    if (!rule) {
      setClosable(true);
      return;
    }

    let closableThreshold;
    if (maintenanceDuration < ONE_HOUR) {
      closableThreshold = rule.lessThan1h;
    } else if (maintenanceDuration <= FOUR_HOURS) {
      closableThreshold = rule.between1And4h;
    } else {
      closableThreshold = rule.moreThan4h;
    }

    if (closableThreshold === 0) {
      setClosable(true);
      return;
    }

    // Check if user has never closed the banner before
    const hasNeverClosedBanner = !localStorage.getItem('maintenanceStripClosingTime');

    if (hasNeverClosedBanner) {
      closableThreshold += FIVE_MINUTES;
    }

    const isNowClosable = countdown > closableThreshold;
    setClosable(isNowClosable);

    // Show the banner on last threadhold even if it was dismissed before
    if (countdown > 0 && !isNowClosable) {
      setBannerClosed(false);
      localStorage.removeItem('maintenanceStripClosingTime');
    }
  };

  useEffect(() => {
    setBannerVisibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, drawerTab, maintenanceDuration, countdownTimer]);

  useEffect(() => {
    showCrossButton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, drawerTab, countdownTimer]);

  const show =
    showBanner &&
    shouldDisplayInMobileTab &&
    (isLoggedIn || isVisibleToUsers) &&
    !bannerClosed;

  if (IS_RN_ANALYTICS_MARKETS()) {
    return null;
  }

  return (
    (<StripPortal isSticky={isStickyTab}>
      <Render when={show}>
        <div
          id="maintenance-strip"
          className={`${styles.maintenanceWrapper} ${
            isTopBanner ? styles.topBanner : ''
          } ${className || ''}
          `}
          style={{
            order: STRIPS_ORDER.MAINTENANCE_STRIP,
          }}
        >
          <div className={styles.desc}>
            <div className={styles.title}>
              <div className={styles.icon}>
                <SmallRectangle className="info-icon" />
              </div>
              <div className={styles.heading}>
                {t('common:maintenance_notif_banner_new')}
                <span className={`${styles.redirectLink}`}>
                  <a
                    data-testid="under-maintenance-url"
                    href={`${UNDER_MAINTENANCE_URL}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <NewRedirectIcon />
                  </a>
                </span>
              </div>
            </div>

            <div className={styles.text}>
              {countdownRef?.current! >= 0 &&
              countdownRef?.current! <= 2 * FIFTEEN_MINUTES ? (
                <div className={styles.timer} data-palette="MobileView">
                  {t('common:startsIn')}
                  {dateFormat(
                    getSeconds(startOf(timeNow(), 'day'), countdownRef?.current!),
                    TIME_FORMATS.mmss
                  )}
                </div>
              ) : (
                <div className={styles.timer} data-palette="MobileView">
                  {maintenanceStartTime}
                  {` `}
                  {!isDatesEqual ? t('common:to_small') : '-'}{' '}
                  {isDatesEqual
                    ? dateFormat(
                        toLocal(toUTC(getDateTime(maintenanceFinishTimeInMilliseconds))),
                        TIME_FORMATS.LT
                      )
                    : maintenanceFinishTime}{' '}
                  ({new Intl.DateTimeFormat().resolvedOptions().timeZone})
                </div>
              )}
            </div>
          </div>
          {isClosable && (
            <div
              aria-hidden="true"
              data-testid="close-icon"
              className={styles.close}
              onClick={onCloseIconClick}
              data-palette="MobileView">
              <CloseIcon />
            </div>
          )}
        </div>
      </Render>
    </StripPortal>)
  );
};

export default memo(MobileView);
